import { Card } from '@ordo/ui/card'
import { cn } from '@ordo/ui/cn'
import { Flame, Snowflake, Thermometer } from 'lucide-react'

import { Badge } from '@/components/chefs/badge'
import { temperatureLabel } from '@/lib/chefs/format'
import { campusMilkSupplyDays, milkGroupBreakdown, milkMealtimeBreakdown, milkSupplyBadge } from '@/lib/chefs/milk'
import { packingRowWords } from '@/lib/chefs/packing'
import type { Campus, CampusMilkLine } from '@/lib/chefs/types'

/**
 * The campus milk card (PROD-1502, Abner's note on Figma 164:874): "Display
 * Milk as a separate card, on top of mealtimes. Badge says "Supply for X
 * days"". One row per milk item, summed across mealtimes and service days,
 * with the per-mealtime split underneath, and, when a split milk schedule put
 * part of a service day on another delivery, how much of it this card carries
 * (PROD-1608), and the split by age group when the campus orders by group
 * (PROD-1624). Renders nothing for a campus with no milk.
 */
export function CampusMilkCard({ campus, className }: { campus: Pick<Campus, 'milk'>; className?: string }) {
  const rows = campus.milk ?? []
  if (rows.length === 0) {
    return null
  }
  const supplyDays = campusMilkSupplyDays(campus)
  return (
    <Card
      data-testid="campus-milk-card"
      className={cn(
        'flex h-full flex-col overflow-hidden rounded-[10px] border-border bg-background py-0 shadow-none',
        className,
      )}>
      <div className="flex flex-wrap items-center gap-2 rounded-t-[10px] border-b border-border bg-card px-4 py-3 text-sm font-medium">
        <span className="text-foreground">Milk</span>
        {supplyDays > 0 ? (
          <Badge variant="info" className="px-2">
            {milkSupplyBadge(supplyDays)}
          </Badge>
        ) : null}
      </div>
      <div className="flex flex-col divide-y divide-border">
        {rows.map((line) => (
          <MilkRow key={line.key} line={line} />
        ))}
      </div>
    </Card>
  )
}

function MilkRow({ line }: { line: CampusMilkLine }) {
  const TempIcon = line.temperature === 'hot' ? Flame : line.temperature === 'ambient' ? Thermometer : Snowflake
  const packing = line.packing && line.packing.length > 0 ? packingRowWords(line.packing, line.servingSize) : null
  const serving = [line.packaging, line.servingSize ? `${line.servingSize} per serving` : null]
    .filter(Boolean)
    .join(' → ')
  const breakdown = milkMealtimeBreakdown(line)
  const byGroup = milkGroupBreakdown(line)
  return (
    <div data-testid="campus-milk-row" className="flex items-center gap-3 px-4 py-2.5 text-sm">
      <span className="flex w-14 shrink-0 flex-col text-xs font-medium text-muted-foreground">
        <span>{line.qty.toLocaleString()}x</span>
        {packing?.countLine ? <span className="font-semibold text-foreground">{packing.countLine}</span> : null}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-foreground">{line.name}</span>
        {packing ? <span className="text-xs font-medium text-foreground">{packing.title}</span> : null}
        {packing?.detail ? (
          <span className="text-xs text-muted-foreground">{packing.detail}</span>
        ) : !packing && serving ? (
          <span className="text-xs text-muted-foreground">{serving}</span>
        ) : null}
        {breakdown ? <span className="text-xs text-muted-foreground">{breakdown}</span> : null}
        {byGroup ? (
          <span data-testid="campus-milk-row-groups" className="text-xs text-muted-foreground">
            {byGroup}
          </span>
        ) : null}
      </span>
      <Badge variant={line.temperature} className="gap-1 px-2">
        <TempIcon className="h-3 w-3" strokeWidth={1.75} />
        {temperatureLabel(line.temperature)}
      </Badge>
    </div>
  )
}
